import type { JSX } from 'react';
import { PILLARS } from '@/lib/pillars';
import { useSessionStore } from '@/state/sessionStore';
import { useModalStore } from '@/state/modalStore';
import { toast } from '@/state/toastStore';
import { Button, Dialog, Field, Select, asOptions } from '../primitives';

/**
 * Shell-level dialogs: filters.
 *
 * Settings moved to /settings (Session + Human Layer sections): it grew a
 * second section that does not fit a modal — a profile form with save/discard
 * state scrolls badly and traps focus, the same reasoning that already moved
 * the account dialog to /account. The account dialog moved there for the same
 * reason: three sections of form, a permission table and a partner list scroll
 * badly in a modal and trap focus around a table, and none of it is a decision
 * you take and dismiss.
 *
 * Feature dialogs (upload, analysis menu, MMM import…) are mounted inside their
 * own features, so a feature's dialog and the state it edits stay together and
 * this host does not become a dependency sink for the whole app.
 */
export function ModalHost(): JSX.Element | null {
  const open = useModalStore((s) => s.open);
  const close = useModalStore((s) => s.close);

  if (open === 'filters') return <FiltersDialog onClose={close} />;
  return null;
}

function FiltersDialog({ onClose }: { readonly onClose: () => void }): JSX.Element {
  const filters = useSessionStore((s) => s.filters);
  const setFilters = useSessionStore((s) => s.setFilters);
  const applyFilters = useSessionStore((s) => s.applyFilters);
  const clearFilters = useSessionStore((s) => s.clearFilters);
  const analyses = useSessionStore((s) => s.analyses);

  const brands = [...new Set(analyses.map((a) => a.brand))].sort((a, b) => a.localeCompare(b));
  const partners = [...new Set(analyses.map((a) => a.partner))].sort((a, b) => a.localeCompare(b));

  return (
    <Dialog
      title="More filters"
      subtitle="Filters apply to the Lockerroom library and research lists."
      onClose={onClose}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              clearFilters();
              onClose();
            }}
          >
            Clear
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const count = applyFilters();
              onClose();
              toast('Filters applied', `${count} filter${count === 1 ? '' : 's'} active`);
            }}
          >
            Apply
          </Button>
        </>
      }
    >
      <div className="grid g2">
        <Field label="Brand">
          <Select
            value={filters.brand}
            placeholder="All brands"
            options={asOptions(brands)}
            onChange={(brand) => {
              setFilters({ brand });
            }}
          />
        </Field>
        <Field label="Partner">
          <Select
            value={filters.partner}
            placeholder="All partners"
            options={asOptions(partners)}
            onChange={(partner) => {
              setFilters({ partner });
            }}
          />
        </Field>
        <Field label="Status">
          <Select
            value={filters.status}
            placeholder="Any status"
            options={[
              { value: 'complete', label: 'Complete' },
              { value: 'draft', label: 'Draft' },
            ]}
            onChange={(status) => {
              setFilters({ status });
            }}
          />
        </Field>
        <Field label="Value pillar">
          <Select
            value={filters.pillar}
            placeholder="All pillars"
            options={PILLARS.map((p) => ({ value: p.id, label: p.label }))}
            onChange={(pillar) => {
              setFilters({ pillar });
            }}
          />
        </Field>
      </div>
    </Dialog>
  );
}
